<div class="pane-back">
  <button type="button" class="link-btn" (click)="onBack()">← Customer migration</button>
</div>

<div class="system-detail">
  <div class="card-header">
    <div class="card-header-text">
      <h2 class="card-title">{{ label }}</h2>
      <p class="card-description">
        @if (system?.state === 'migrated') {
          This car wash has cut over from {{ label }}. Kept here as a record — imported customers,
          memberships and vehicles are unaffected.
        } @else if (isConnected) {
          A temporary bridge while this car wash runs {{ label }} alongside Way.
          @if (isPaused) {
            Paused — not syncing until you turn it back on.
          } @else if (!isNightlySyncOn) {
            Nightly sync is off — it only syncs when you click Sync now.
          } @else {
            Pulled nightly at 05:30 PT until you cut over.
          }
        } @else {
          Sign in once and we will pull their member roster over, keeping it current until they cut
          over.
        }
      </p>
    </div>
    @if (system) {
      <span class="pill" [attr.data-tone]="system.tone">{{ system.statusLabel }}</span>
    } @else {
      <span class="pill">Not connected</span>
    }
  </div>

  <!--
    Settings lead because they are what an operator comes here to change; the
    groups below are mostly read-only once a bridge is running.

    This screen deliberately does NOT present the bridge as a wizard. It once had
    a Connect -> Map plans -> Backfill -> Bridge -> Cut over rail with numbered
    steps, which misdescribed the thing: a bridge is a standing arrangement an
    operator returns to and adjusts, not a sequence completed once. The numbering
    also implied the sections had to be visited in order, when in practice
    credentials, plan mapping and the roster are edited independently and
    revisited for months. The status pill in the header carries where the bridge
    stands; each group's summary line carries whether its own part is in order.
  -->
  @if (isConnected && system?.state !== 'migrated') {
    <section class="settings-panel" aria-label="Bridge settings">
      <div class="settings-head">
        <h3 class="settings-title">Settings</h3>
        <p class="settings-subtitle">
          Redemption settings stay in force even when the roster sync is off.
        </p>
      </div>

      @if (plateMatchingError) {
        <div class="alert alert-error" role="alert">{{ plateMatchingError }}</div>
      }

      <!--
        CWS-1369, first because it is the one setting here that changes what an attendant
        sees rather than what happens at the gate — and the only one that applies at the
        counter whether or not a camera is involved. Its own row, not a child of automatic
        redemption: the two are independent, a shop can scan barcodes with auto-redeem off
        or auto-redeem at the lane while the order form shows nothing.
      -->
      <div class="ending-row">
        <div class="summary-text">
          <span class="summary-title">Show Redeem Existing Membership on the order form</span>
          <span class="summary-hint">
            Gives the attendant a barcode reader and a camera scanner to look a member up by their
            card. Turn it off where {{ label }} members are identified by license plate instead —
            the scanner has no token to match against, and the buttons only get in the way.
          </span>
        </div>
        <div class="ending-toggle">
          <mat-slide-toggle
            [checked]="plateMatching.orderFormRedeem"
            [disabled]="isSavingPlateMatching"
            (change)="onToggleOrderFormRedeem()"
            aria-label="Show Redeem Existing Membership on the order form"
          ></mat-slide-toggle>
          @if (isSavingPlateMatching) {
            <span class="toggle-status">Saving…</span>
          }
        </div>
      </div>

      <!--
        Redemption locations belong to automatic redemption, indented under it rather than
        sitting beside it: both govern whether an imported membership redeems at the gate.
        (The order-form switch above is a different question and deliberately outside this
        group.) Kept visible when automatic redemption is off, because the allowlist is a
        hard boundary on barcode redemption too — hiding it would imply it only applies to
        camera reads.
      -->
      <div class="setting-group">
        <div class="ending-row">
          <div class="summary-text">
            <span class="summary-title">Enable automatic membership redemption</span>
            <span class="summary-hint">
              A camera plate read redeems the membership and completes the visit. When off, the
              visit becomes an order request for an attendant — a barcode scan still redeems either
              way.
            </span>
          </div>
          <div class="ending-toggle">
            <mat-slide-toggle
              [checked]="plateMatching.autoRedeem"
              [disabled]="isSavingPlateMatching"
              (change)="onToggleAutoRedeem()"
              aria-label="Enable automatic membership redemption"
            ></mat-slide-toggle>
            @if (isSavingPlateMatching) {
              <span class="toggle-status">Saving…</span>
            }
          </div>
        </div>

        <div class="setting-child">
          <div class="redeem-locations">
            <div class="redeem-locations-head">
              <span class="summary-title">Redemption locations</span>
              <span class="summary-hint">
                Limit where imported {{ label }} memberships redeem, by camera or barcode. Select
                none to allow every location.
              </span>
            </div>

            @if (shopLocationsLoading) {
              <p class="muted">Loading locations…</p>
            } @else if (!shopLocations.length) {
              <p class="field-hint">
                No active locations found for this car wash, so the allowlist cannot be edited here.
                The toggle above still applies.
              </p>
            } @else {
              <div class="redeem-location-list">
                @for (loc of shopLocations; track loc.id) {
                  <label
                    class="redeem-location"
                    [class.on]="isPlateMatchingLocationSelected(loc.id)"
                  >
                    <input
                      type="checkbox"
                      [checked]="isPlateMatchingLocationSelected(loc.id)"
                      [disabled]="isSavingPlateMatching"
                      (change)="onToggleRedeemLocation(loc.id)"
                    />
                    <span>{{ loc.branch || loc.id }}</span>
                  </label>
                }
              </div>

              <div class="actions">
                @if (isPlateMatchingLocationRestricted) {
                  <button
                    type="button"
                    class="link-btn"
                    (click)="onClearRedeemLocations()"
                    [disabled]="isSavingPlateMatching"
                  >
                    Allow every location
                  </button>
                } @else {
                  <span class="field-hint"> Unrestricted — redeems at every location. </span>
                }
              </div>
            }
          </div>
        </div>

        <div
          class="alert"
          [class.alert-success]="plateMatching.autoRedeem"
          [class.alert-warning]="!plateMatching.autoRedeem"
          role="status"
        >
          <strong>What happens at the gate now:</strong> {{ plateMatchingSummary }}
        </div>
      </div>

      <div class="ending-row">
        <div class="summary-text">
          <span class="summary-title">Sync enabled</span>
          <span class="summary-hint">
            Pausing stops nightly and manual syncs for {{ label }} and hides it in the staff app.
            Credentials and plan mapping are kept — use Cut over to finish the migration.
          </span>
        </div>
        <div class="ending-toggle">
          <mat-slide-toggle
            [checked]="!!credentialStatus?.enabled"
            [disabled]="isTogglingSyncEnabled || isJobRunning"
            (change)="onToggleSyncEnabled()"
            aria-label="Sync enabled"
          ></mat-slide-toggle>
          @if (isTogglingSyncEnabled) {
            <span class="toggle-status">Saving…</span>
          }
        </div>
      </div>

      <div class="ending-row">
        <div class="summary-text">
          <span class="summary-title">Run nightly at 05:30 PT</span>
          <span class="summary-hint">
            @if (isPaused) {
              The sync is paused, which already stops the nightly run.
            } @else {
              A full sync of the {{ label }} roster every night, wherever this sync runs. Turn off
              only for a canary; Sync now still works.
            }
          </span>
        </div>
        <div class="ending-toggle">
          <mat-slide-toggle
            data-testid="nightly-sync-toggle"
            [checked]="isNightlySyncOn"
            [disabled]="isTogglingNightlySync || isJobRunning || isPaused"
            (change)="onToggleNightlySync()"
            aria-label="Run nightly at 05:30 PT"
          ></mat-slide-toggle>
          @if (isTogglingNightlySync) {
            <span class="toggle-status">Saving…</span>
          }
        </div>
      </div>

      <div class="ending-row ending-row--stacked">
        <div class="summary-text">
          <span class="summary-title">Where this sync runs</span>
          @if (agentCapable) {
            <span class="summary-hint">
              Every adapter can run in either place. Only pick the agent machine when the vendor
              refuses Way's cloud egress — it has to be on and awake, and one machine is a smaller
              thing to rely on than the cluster.
            </span>
          } @else {
            <!-- Unreachable for a registered adapter now that every one is
                 agent-capable. Kept because the descriptor is the authority:
                 a backend that reports agentCapable:false must not get a
                 picker whose only outcome is a 400. -->
            <span class="summary-hint">
              Cloud worker. Agent execution is not available for {{ label }} — the agent has no
              {{ label }} adapter.
            </span>
          }
          @if (executorError) {
            <span class="summary-hint" role="alert">{{ executorError }}</span>
          }
        </div>

        @if (agentCapable) {
          <!-- Two cards rather than two buttons: the choice has consequences an
               operator cannot infer from the words "Server" and "Agent", and the
               old toggle showed only for iWashPOS so the question never came up.
               Now that every vendor offers it, the picker has to carry the
               reason. -->
          <div
            class="executor-picker"
            role="radiogroup"
            aria-label="Where this sync runs"
            [attr.aria-busy]="isSavingExecutor"
          >
            <button
              type="button"
              class="executor-option"
              role="radio"
              [attr.aria-checked]="executor === 'server'"
              [class.is-selected]="executor === 'server'"
              [disabled]="isSavingExecutor"
              (click)="onSetExecutor('server')"
            >
              <span class="executor-option__head">
                <span class="executor-option__name">Way cloud</span>
                @if (recommendedExecutor === 'server') {
                  <span class="pill" data-tone="ok">Recommended</span>
                }
              </span>
              <span class="executor-option__body">
                The in-cluster worker pulls on the nightly sweep. Nothing to keep running.
              </span>
            </button>

            <button
              type="button"
              class="executor-option"
              role="radio"
              [attr.aria-checked]="executor === 'agent'"
              [class.is-selected]="executor === 'agent'"
              [class.is-risky]="agentWouldBeRefused"
              [disabled]="isSavingExecutor"
              (click)="onSetExecutor('agent')"
            >
              <span class="executor-option__head">
                <span class="executor-option__name">Agent machine</span>
                @if (recommendedExecutor === 'agent') {
                  <span class="pill" data-tone="ok">Recommended</span>
                }
              </span>
              <span class="executor-option__body">
                @switch (agentCapabilityState) {
                  @case ('capable') {
                    {{ connectedAgentSummary }} — connected and ships the {{ label }} adapter.
                  }
                  @case ('incapable') {
                    {{ connectedAgentNames }} is connected but does <strong>not</strong> ship a
                    {{ label }} adapter. A sync set to the agent machine will be refused until it is
                    rebuilt on a newer image.
                  }
                  @case ('unknown') {
                    {{ connectedAgentSummary }} — connected, but too old to report which adapters it
                    has. Whether it can run {{ label }} will only show up when a sync runs.
                  }
                  @default {
                    {{ connectedAgentSummary }}. A sync set here is queued and waits, so this is
                    safe to set before the agent machine is started.
                  }
                }
              </span>
            </button>
          </div>

          @if (executor === 'agent' && !isOnRecommendedExecutor) {
            <div class="alert alert-warning" role="status">
              {{ label }} pulls fine from the Way cloud, so the agent machine is doing work the
              cluster would do more reliably. Switch back to Way cloud unless this car wash's
              {{ label }} blocks our egress.
            </div>
          }

          @if (agentJob) {
            <p class="field-hint">
              Latest agent job: {{ agentJob.status }}
              @if (agentJob.errorMessage) {
                — {{ agentJob.errorMessage }}
              }
            </p>
          }
        }
      </div>
    </section>
  }

  <div class="card-body">
    @if (credentialError) {
      <div class="alert alert-error" role="alert">{{ credentialError }}</div>
    }

    @if (credentialStatus && !credentialStatus.encryptionConfigured) {
      <div class="alert alert-warning" role="status">
        Credential encryption is not configured on this environment
        (<code>VENDOR_SYNC_ENCRYPTION_KEY</code>), so credentials cannot be saved. You can still
        verify a login below.
      </div>
    }

    @if (credentialStatus?.unavailableReason) {
      <div class="alert alert-warning" role="status">
        Credential storage is unavailable: {{ credentialStatus?.unavailableReason }}
      </div>
    }

    @if (system?.state !== 'migrated') {
      <!-- ── Connect ────────────────────────────────────────────────────── -->
      <section class="section">
        <button
          type="button"
          class="section-toggle"
          (click)="toggleSection('connect')"
          [attr.aria-expanded]="isSectionOpen('connect')"
        >
          <span
            class="section-caret"
            [class.open]="isSectionOpen('connect')"
            aria-hidden="true"
          ></span>
          <span class="section-toggle-text">
            <span class="section-title">Credentials</span>
            <span class="section-summary">{{ connectSummary }}</span>
          </span>
        </button>

        @if (isSectionOpen('connect')) {
          <p class="section-subtitle">
            The same credentials the car wash uses. Stored encrypted against this car wash and used
            only by the scheduled sync — never shown again after saving.
          </p>

          @if (credentialStatusLoading) {
            <p class="muted">Checking connection…</p>
          } @else if (isConnected && !showCredentialForm) {
            <div class="summary-row">
              <div class="summary-text">
                <span class="summary-title">{{ label }} is connected</span>
                <span class="summary-hint">
                  Stored credentials are used automatically.
                  @if (credentialStatus?.locationCode) {
                    Location <strong>{{ credentialStatus?.locationCode }}</strong
                    >.
                  }
                  @if (credentialStatus?.lastVerifiedAt) {
                    Last verified {{ formatDate(credentialStatus?.lastVerifiedAt) }}
                  }
                  @if (credentialStatus?.updatedBy) {
                    by {{ credentialStatus?.updatedBy }}.
                  }
                </span>
              </div>
              <button type="button" class="secondary-btn small" (click)="onToggleCredentialForm()">
                Change credentials
              </button>
            </div>
          } @else {
            <div class="field-grid">
              @for (field of credentialFields; track trackByKey($index, field)) {
                <div class="form-field">
                  <label class="field-label" [for]="'cred-' + field.key">{{ field.label }}</label>
                  <input
                    [id]="'cred-' + field.key"
                    [type]="field.type"
                    class="form-input"
                    [name]="'cred_' + field.key"
                    [(ngModel)]="credentialValues[field.key]"
                    [disabled]="!canEditCredentials"
                    [autocomplete]="field.type === 'password' ? 'new-password' : 'off'"
                    [placeholder]="field.hint || ''"
                  />
                  @if (field.type === 'password') {
                    <p class="field-hint">Never displayed again after saving.</p>
                  } @else if (field.hint) {
                    <p class="field-hint">{{ field.hint }}</p>
                  }
                </div>
              }
            </div>

            @if (credentialTestResult?.ok) {
              <div class="alert alert-success" role="status">
                <strong>
                  Connected
                  @if (credentialTestResult?.location) {
                    to {{ credentialTestResult?.location }}
                  }
                </strong>
                <!-- Not truthiness: a site reporting 0 members is exactly the
                     wrong-site case this sentence exists to catch, and 0 is falsy.
                     See hasTestMemberCount for why the check is not inline. -->
                @if (hasTestMemberCount) {
                  — {{ label }} reports
                  <strong>{{ credentialTestResult?.memberCount }} members</strong> at this site.
                  Check that against what the car wash expects before saving: it is the last chance
                  to catch the wrong site.
                }
                @if (credentialTestResult?.expiresAt) {
                  Session valid until {{ formatDate(credentialTestResult?.expiresAt) }}.
                }
              </div>
            }

            <div class="actions">
              <button
                type="button"
                class="secondary-btn"
                (click)="onTestCredentials()"
                [disabled]="!hasCredentialInput || isTestingCredentials || isSavingCredentials"
              >
                {{ isTestingCredentials ? 'Verifying…' : 'Test connection' }}
              </button>
              @if (canEditCredentials) {
                <button
                  type="button"
                  class="primary-btn"
                  (click)="onSaveCredentials()"
                  [disabled]="!hasCredentialInput || isSavingCredentials || isTestingCredentials"
                >
                  {{
                    isSavingCredentials
                      ? 'Saving…'
                      : isConnected
                        ? 'Save credentials'
                        : 'Save and continue'
                  }}
                </button>
              }
              @if (isConnected) {
                <button type="button" class="link-btn" (click)="onToggleCredentialForm()">
                  Cancel — keep the stored credentials
                </button>
              }
            </div>
          }
        }
      </section>

      <!-- ── Plan mapping ───────────────────────────────────────────────── -->
      <section class="section">
        <button
          type="button"
          class="section-toggle"
          (click)="toggleSection('plans')"
          [attr.aria-expanded]="isSectionOpen('plans')"
        >
          <span
            class="section-caret"
            [class.open]="isSectionOpen('plans')"
            aria-hidden="true"
          ></span>
          <span class="section-toggle-text">
            <span class="section-title">Plan mapping</span>
            <span class="section-summary">{{ plansSummary }}</span>
          </span>
        </button>

        @if (isSectionOpen('plans')) {
          <p class="section-subtitle">
            Each {{ label }} plan name needs a Way package. A plan with no valid mapping still
            imports its members — they just arrive with nothing to redeem at the gate.
          </p>

          @if (planMappingLoading) {
            <p class="muted">Loading plan mapping…</p>
          } @else {
            @if (planMapping && !planMapping.has_credentials) {
              <div class="alert alert-warning" role="status">
                Save {{ label }} credentials above before a mapping can be saved. You can still
                build it now — it just won’t save until then.
              </div>
            }

            <div class="mapping-stats">
              <div class="mapping-stat">
                <span class="mapping-stat-value">{{ planMappingRows.length }}</span>
                <span class="mapping-stat-label">plans mapped</span>
              </div>
              <div class="mapping-stat">
                <span class="mapping-stat-value ok">{{
                  planMappingRows.length - invalidMappings
                }}</span>
                <span class="mapping-stat-label">resolve to a live package</span>
              </div>
              <div class="mapping-stat">
                <span class="mapping-stat-value" [class.warn]="invalidMappings > 0">
                  {{ invalidMappings }}
                </span>
                <span class="mapping-stat-label">will silently skip members</span>
              </div>
            </div>

            @if (planMappingRows.length) {
              <div class="mapping-rows">
                @for (row of planMappingRows; track row; let i = $index) {
                  <div class="mapping-row" [class.invalid]="!row.valid">
                    <span class="mapping-plan">{{ row.planName }}</span>
                    <select
                      class="form-input"
                      [(ngModel)]="row.packageId"
                      (ngModelChange)="onPlanMappingRowChanged(row)"
                      name="mappingPackage{{ i }}"
                      [disabled]="isSavingPlanMapping"
                    >
                      <option value="" disabled>Select a package</option>
                      @for (pkg of planMapping?.available_packages; track pkg.id) {
                        <option [value]="pkg.id">{{ pkg.package_name }}</option>
                      }
                    </select>
                    @if (!row.valid) {
                      <span class="mapping-reason">{{
                        describePlanMappingReason(row.reason)
                      }}</span>
                    } @else {
                      <span class="mapping-reason ok">Valid</span>
                    }
                    <button
                      type="button"
                      class="link-btn danger"
                      (click)="onRemovePlanMappingRow(i)"
                      [disabled]="isSavingPlanMapping"
                    >
                      Remove
                    </button>
                  </div>
                }
              </div>
            } @else {
              <p class="muted">No plans mapped yet.</p>
            }

            @if (invalidMappings > 0) {
              <div class="alert alert-warning" role="status">
                <strong>An invalid mapping fails closed, exactly like no mapping.</strong> Those
                members import as customers and are then skipped for entitlement. Fix the package or
                point the plan elsewhere before the next nightly sync.
              </div>
            }

            <div class="add-plan">
              <div class="add-plan-heading">
                <span class="summary-title">Add a plan</span>
                <p class="field-hint">
                  Plan names cannot be listed for you — type them exactly as {{ label }} spells
                  them, including case and spacing. A near-miss is not flagged anywhere: it simply
                  never matches, and those members import with no entitlement.
                </p>
              </div>
              <div class="add-plan-fields">
                <input
                  type="text"
                  class="form-input"
                  name="newPlanName"
                  placeholder="Plan name, e.g. Unlimited Champion"
                  [(ngModel)]="newPlanName"
                  [disabled]="isSavingPlanMapping"
                  autocomplete="off"
                />
                <select
                  class="form-input"
                  name="newPackageId"
                  [(ngModel)]="newPackageId"
                  [disabled]="isSavingPlanMapping"
                >
                  <option value="" disabled selected>Select a package</option>
                  @for (pkg of planMapping?.available_packages; track pkg.id) {
                    <option [value]="pkg.id">{{ pkg.package_name }}</option>
                  }
                </select>
                <button
                  type="button"
                  class="secondary-btn"
                  (click)="onAddPlanMappingRow()"
                  [disabled]="!newPlanName.trim() || !newPackageId || isSavingPlanMapping"
                >
                  Add plan
                </button>
              </div>
            </div>

            <div class="actions">
              <button
                type="button"
                class="primary-btn"
                (click)="onSavePlanMapping()"
                [disabled]="!planMapping?.has_credentials || isSavingPlanMapping"
              >
                {{ isSavingPlanMapping ? 'Saving…' : 'Save mapping' }}
              </button>
              <span class="field-hint">Saving replaces the whole mapping for {{ label }}.</span>
            </div>

            @if (planMappingSaved) {
              <div class="alert alert-success" role="status">Plan mapping saved.</div>
            }
            @if (planMappingError) {
              <div class="alert alert-error" role="alert">{{ planMappingError }}</div>
            }
          }
        }
      </section>

      <!-- ── Sync ───────────────────────────────────────────────────────── -->
      <section class="section">
        <button
          type="button"
          class="section-toggle"
          (click)="toggleSection('roster')"
          [attr.aria-expanded]="isSectionOpen('roster')"
        >
          <span
            class="section-caret"
            [class.open]="isSectionOpen('roster')"
            aria-hidden="true"
          ></span>
          <span class="section-toggle-text">
            <span class="section-title">Roster sync</span>
            <span class="section-summary">{{ rosterSummary }}</span>
          </span>
        </button>

        @if (isSectionOpen('roster')) {
          @if (isManualOnly) {
            <div class="alert alert-warning" role="status" data-testid="manual-only-warning">
              Nightly sync is off — {{ label }} only syncs when you click Sync now. Turn it on once
              the canary passes.
            </div>
          } @else {
            <p class="section-subtitle">
              Runs nightly on its own. Start one now to backfill, or to check a change straight
              away.
            </p>
          }

          <div class="actions sync-actions">
            <div class="form-field sync-limit-field">
              <label class="field-label" for="sync-limit">Row limit per phase (optional)</label>
              <input
                id="sync-limit"
                type="number"
                class="form-input"
                name="syncLimit"
                min="1"
                [max]="maxSyncLimit"
                step="1"
                placeholder="All rows"
                [(ngModel)]="syncLimit"
                (input)="onSyncLimitInput($event)"
                [disabled]="isJobRunning || isStartingSync"
                [attr.aria-invalid]="!isSyncLimitValid"
              />
            </div>
            <button
              type="button"
              class="primary-btn"
              (click)="onStartSync()"
              [disabled]="!canStartSync"
            >
              @if (isStartingSync) {
                Starting…
              } @else if (isJobRunning) {
                Sync running…
              } @else {
                Sync now
              }
            </button>
            @if (!isConnected) {
              <span class="field-hint">Save credentials above before a sync can start.</span>
            } @else if (isPaused) {
              <span class="field-hint">This bridge is paused. Resume it below to sync.</span>
            }
          </div>
          @if (isSyncLimitValid) {
            <p class="field-hint">
              Blank runs a full sync. A number caps each import phase — e.g. 10 imports up to 10
              customers and up to 10 memberships for a vendor that imports them separately
              (iWashPOS). Vendors with a single import are capped at that many rows in total.
            </p>
          } @else {
            <div class="alert alert-error" role="alert">
              Row limit must be a whole number from 1 to {{ maxSyncLimit }}.
            </div>
          }

          @if (showProgressPanel) {
            <div class="progress-panel" aria-live="polite">
              <div class="progress-head">
                <span class="summary-title">{{ label }} sync</span>
                @if (agentJobId) {
                  <span class="pill" data-tone="info">On agent machine</span>
                }
                @if (isNightlySync) {
                  <span class="pill" data-tone="info" data-testid="sync-trigger-pill"
                    >Nightly sync</span
                  >
                }
                @if (displayedSyncLimit) {
                  <span class="pill" data-tone="info" data-testid="sync-limit-pill"
                    >Limited to {{ displayedSyncLimit }} rows per phase</span
                  >
                }
                <span class="pill" [attr.data-tone]="jobStateTone" data-testid="sync-job-state">
                  {{ isJobRunning ? 'In progress' : jobState }}
                </span>
              </div>

              @if (isAwaitingAgentPickup) {
                <!-- Nothing is talking to the vendor yet: the job is queued and
                     no agent has claimed it. Usually the agent is not
                     running, which the operator can only fix if we say so. -->
                <p class="muted" role="status">
                  Queued for the agent machine — {{ connectedAgentSummary }}. Counts start moving
                  once an agent claims it. If none is connected, start the agent on the agent
                  machine; the job is held for 24 hours.
                </p>
              } @else if (isAwaitingFirstPage) {
                <p class="muted" role="status">
                  Waiting for {{ label }} to return the first members. The counts below start moving
                  once they arrive.
                </p>
              }

              @if (percent !== null) {
                <div class="progress-track">
                  <div class="progress-fill" [style.width.%]="percent"></div>
                </div>
                <p class="field-hint">
                  {{ jobProgress?.fetched ?? 0 }} of {{ reportTotal }} fetched
                </p>
              } @else {
                <p class="field-hint">
                  No percentage until {{ label }} reports a total for this site — until then there
                  is no honest denominator.
                </p>
              }

              <dl class="job-stats">
                <div class="job-stat">
                  <dt>Fetched</dt>
                  <dd>{{ jobProgress?.fetched ?? 0 }}</dd>
                </div>
                <div class="job-stat">
                  <dt>Imported</dt>
                  <dd>{{ jobProgress?.inserted ?? 0 }}</dd>
                </div>
                <div class="job-stat">
                  <dt>Updated</dt>
                  <dd>{{ jobProgress?.updated ?? 0 }}</dd>
                </div>
                <div class="job-stat" [class.warn]="skippedCount > 0">
                  <dt>Skipped</dt>
                  <dd>{{ skippedCount }}</dd>
                </div>
              </dl>

              @if (skippedCount > 0) {
                <div class="alert alert-warning" role="status">
                  {{ skippedCount }} member{{ skippedCount === 1 ? '' : 's' }} could not be matched
                  to a Way customer, so nothing was created for them. This is the vendor’s data, not
                  an error — the adapter reports the count only, so there is no list to download.
                </div>
              }

              @if (jobFailedReason) {
                <div class="alert alert-error">{{ jobFailedReason }}</div>
              }
            </div>
          }

          <div class="sync-history">
            <h4 class="sync-history-title">Past syncs</h4>

            @if (hasSyncHistory) {
              <div class="sync-history-wrap">
                <table class="sync-history-table">
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      <th scope="col">Status</th>
                      <th scope="col" class="numeric">Processed</th>
                      <th scope="col" class="numeric">Inserted</th>
                      <th scope="col" class="numeric">Updated</th>
                      <th scope="col" class="sync-history-csv">CSV</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (run of syncHistory; track run.id) {
                      <tr>
                        <td>{{ run.when }}</td>
                        <td>
                          <span class="pill" [attr.data-tone]="run.tone">{{ run.status }}</span>
                        </td>
                        @if (run.counts) {
                          <td class="numeric">{{ run.counts.processed }}</td>
                          <td class="numeric">{{ run.counts.inserted }}</td>
                          <td class="numeric">{{ run.counts.updated }}</td>
                        } @else {
                          <td class="sync-history-note" colspan="3">{{ run.note }}</td>
                        }
                        <td class="sync-history-csv">
                          @if (run.csvUrl) {
                            <a
                              class="sync-history-csv-link"
                              [href]="run.csvUrl"
                              target="_blank"
                              rel="noopener noreferrer"
                              [attr.download]="run.csvName"
                              [attr.aria-label]="'Download the CSV from the ' + run.when + ' sync'"
                            >
                              Download
                            </a>
                          } @else {
                            <span
                              class="sync-history-csv-none"
                              title="No file was uploaded for this run"
                              >—</span
                            >
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              @if (isSyncHistoryTruncated) {
                <p class="field-hint">
                  Showing the most recent runs only — older syncs for {{ label }} may exist beyond
                  this window.
                </p>
              }
            } @else {
              <p class="field-hint">
                @if (isSyncHistoryTruncated) {
                  No {{ label }} syncs inside the recent history window. Older ones may exist.
                } @else {
                  No syncs yet. The first one will appear here once it finishes.
                }
              </p>
            }
          </div>
        }
      </section>

      <!-- ── Ending ─────────────────────────────────────────────────────── -->
      @if (isConnected) {
        <div class="divider"></div>

        <section class="section">
          <button
            type="button"
            class="section-toggle"
            (click)="toggleSection('finish')"
            [attr.aria-expanded]="isSectionOpen('finish')"
          >
            <span
              class="section-caret"
              [class.open]="isSectionOpen('finish')"
              aria-hidden="true"
            ></span>
            <span class="section-toggle-text">
              <span class="section-title">Cut over</span>
              <span class="section-summary">{{ finishSummary }}</span>
            </span>
          </button>

          @if (isSectionOpen('finish')) {
            <p class="section-subtitle">
              A bridge is temporary. When this car wash no longer sells memberships in {{ label }},
              cut over so Way is the only source of truth.
            </p>

            @if (system?.transitionNote) {
              <p class="field-hint">{{ system?.transitionNote }}</p>
            }

            @if (cutOverReady) {
              <div class="alert alert-success" role="status">
                <strong>This bridge looks finished.</strong> {{ system?.summary }}
              </div>
            }

            <div class="ending-row cut-over">
              <div class="summary-text">
                <span class="summary-title">Cut over from {{ label }}</span>
                <span class="summary-hint">
                  Ends the migration. The nightly sync stops and the stored credentials are deleted.
                  Imported customers, memberships and vehicles are kept.
                </span>
              </div>
              <button
                type="button"
                class="primary-btn"
                (click)="onOpenCutOver()"
                [disabled]="isJobRunning"
              >
                Cut over…
              </button>
            </div>
          }
        </section>
      }
    } @else {
      <div class="alert alert-success" role="status">
        <strong>Migrated.</strong> {{ system?.summary }} Reconnecting is possible, but the
        credentials were deleted, so they would need to be entered again.
      </div>
    }
  </div>
</div>

@if (showCutOver) {
  <div class="picker-backdrop" (click)="onCancelCutOver()"></div>
  <div class="cutover-modal" role="dialog" aria-modal="true" aria-labelledby="cutover-title">
    <div class="cutover-head">
      <h3 class="picker-title" id="cutover-title">Cut over from {{ label }}?</h3>
      <p class="picker-subtitle">
        Way becomes the only source of truth for this car wash’s members. This is the intended end
        of the migration, not a pause.
      </p>
    </div>

    <ul class="cutover-list">
      <li class="keep">
        Their imported customers, memberships and vehicles
        <strong>stay exactly as they are</strong>.
      </li>
      <li>The nightly sync stops. Nothing new arrives from {{ label }}.</li>
      <li>
        Stored {{ label }} credentials are <strong>deleted</strong>. Reconnecting later means
        re-entering them.
      </li>
      <li>Any {{ label }} plate matching and auto-redeem stops applying at the lane.</li>
    </ul>

    <div class="alert alert-warning cutover-warning" role="status">
      If the car wash is still selling memberships in {{ label }}, those members will never reach
      Way. Confirm they have stopped before you cut over.
    </div>

    <div class="cutover-confirm">
      <label class="field-label" for="cutover-input">
        Type <strong>{{ cutOverExpectedText }}</strong> to confirm
      </label>
      <input
        id="cutover-input"
        type="text"
        class="form-input"
        name="cutOverConfirmation"
        [(ngModel)]="cutOverConfirmation"
        autocomplete="off"
        placeholder="Car wash name"
      />
    </div>

    <div class="cutover-actions">
      <button type="button" class="secondary-btn" (click)="onCancelCutOver()">Keep bridging</button>
      <button
        type="button"
        class="primary-btn"
        (click)="onConfirmCutOver()"
        [disabled]="!cutOverConfirmed || isCuttingOver"
      >
        {{ isCuttingOver ? 'Cutting over…' : 'Cut over and disconnect' }}
      </button>
    </div>
  </div>
}
